import React, { FC, HTMLAttributes, MouseEventHandler } from "react";

import { GatsbyLinkProps } from "gatsby";
import { FocusRing, useFocusRing } from "react-aria";

import { css } from "@emotion/react";
import styled from "@emotion/styled";

import theme, { Theme } from "../../../Theme";
import { Link as Link_ } from "../../LinkComponent";
import LoadingIndicator from "../../LoadingIndicator";

const Link = Link_ as FC<GatsbyLinkProps<any>>;

const CenteredButtonContainer = styled.div`
    display: flex;
    justify-content: center;
`;

export enum ButtonColor {
    "red" = "red",
    "blue" = "blue",
    "grey" = "grey",
    "blueOutline" = "blueOutline",
    "whiteOutline" = "whiteOutline",
}

export type ButtonColorStr = `${ButtonColor}`;

const extend =
    <k extends string | symbol, k2 extends k>() =>
    <v,>(
        r: Record<k2, v>
    ): Record<k2, v> & Partial<Record<Exclude<k, k2>, v>> =>
        r as any;

const baseStyle = (
    theme: Theme,
    color: ButtonColor | ButtonColorStr | keyof Theme["colors"]
) => {
    const buttonStyles = extend<typeof color, ButtonColorStr>()({
        blue: css`
            color: white;
        `,

        red: css`
            color: white;
        `,

        grey: css`
            color: white;
        `,

        blueOutline: css`
            background-color: transparent;
        `,
        whiteOutline: css`
            background-color: transparent;
        `,
    });

    return buttonStyles[color] ?? buttonStyles.blue;
};

const buttonStyle = (
    theme: Theme,
    color: ButtonColor | ButtonColorStr | keyof Theme["colors"]
) => {
    const buttonStyles = extend<typeof color, ButtonColorStr>()({
        blue: css`
            color: white !important;
            background-color: ${theme.colors.blue.toString()};
            border-color: ${theme.colors.blue.toString()};
        `,

        red: css`
            background-color: ${theme.colors.red.toString()};
            border-color: ${theme.colors.red.toString()};
        `,

        grey: css`
            background-color: ${theme.colors.darkGrey.toString()};
            border-color: ${theme.colors.darkGrey.toString()};
        `,

        blueOutline: css`
            color: ${theme.colors.blue.toString()};
            border: 2px solid ${theme.colors.blue.toString()};
        `,
        whiteOutline: css`
            color: ${theme.colors.white.toString()};
            border: 2px solid ${theme.colors.white.toString()};
        `,
    });

    return buttonStyles[color] ?? buttonStyles.blue;
};

export type ButtonProps = {
    isBold?: boolean;
    fontSize?: string;
    fullWidth?: boolean;
    centered?: boolean;
    loading?: boolean;
    disabled?: boolean;
    small?: boolean;
    extraSmall?: boolean;
} & (
    | ({ to: string } & GatsbyLinkProps<any>)
    | ({ htmlFor: string } & HTMLAttributes<HTMLLabelElement>)
    | ({
          onClick: MouseEventHandler<HTMLButtonElement>;
      } & HTMLAttributes<HTMLButtonElement>)
    | ({ disabled: true } & HTMLAttributes<HTMLButtonElement>)
    | ({
          type: "span";
      } & HTMLAttributes<HTMLSpanElement>)
    | ({
          type: "submit" | "button" | "reset";
      } & HTMLAttributes<HTMLButtonElement>)
) &
    (
        | {
              text: true;
              color?: keyof Theme["colors"];
          }
        | {
              text?: false;
              color?: ButtonColorStr;
          }
        | {
              text: boolean;
              color?: undefined;
          }
    );

const defaultProps = {
    color: "blue",
    fontSize: "1rem",
    fullWidth: false,
    centered: false,
    text: false,
    small: false,
    extraSmall: false,
    loading: false,
} as const;

const RawButton: FC<ButtonProps> = ({
    color,
    children,
    centered = defaultProps.centered,
    text,
    loading = defaultProps.loading,
    ...props
}) => {
    const { isFocusVisible, focusProps } = useFocusRing();

    const content = (
        <>
            {!loading && children}
            {loading && (
                <LoadingIndicator
                    light={text || !(color as ButtonColor).includes("Outline")}
                />
            )}
        </>
    );

    const focusStyle = isFocusVisible
        ? { outline: "2px solid #05235f", outlineOffset: 6, borderRadius: 4 }
        : {};

    const button =
        "to" in props ? (
            <Link {...props} {...focusProps} style={focusStyle}>
                {content}
            </Link>
        ) : "htmlFor" in props ? (
            <label {...props} {...focusProps} style={focusStyle}>
                {content}
            </label>
        ) : (props as any).type === "span" ? (
            <span {...props} {...focusProps} style={focusStyle} tabIndex={0}>
                {content}
            </span>
        ) : (
            <button {...props} {...focusProps} style={focusStyle}>
                {content}
            </button>
        );

    if (centered) {
        return <CenteredButtonContainer>{button}</CenteredButtonContainer>;
    }

    return button;
};

RawButton.defaultProps = defaultProps as any;

const Button = styled(RawButton)<ButtonProps>`
    font-size: ${({
        small = defaultProps.small,
        extraSmall = defaultProps.extraSmall,
        fontSize = defaultProps.fontSize,
    }) => (small ? "1rem" : extraSmall ? "0.75rem" : fontSize)};
    line-height: ${({
        small = defaultProps.small,
        extraSmall = defaultProps.extraSmall,
        fontSize = defaultProps.fontSize,
    }) => (small ? "1rem" : extraSmall ? "0.75rem" : fontSize)};
    padding: 15px 40px;
    border-radius: 4px;
    border-style: solid;
    width: fit-content;
    cursor: pointer;
    text-align: center;
    font-weight: 200;

    .focus-ring {
        border-radius: 4px;
        outline: 2px solid #05235f;
        outline-offset: 6px;
    }

    &:hover {
        text-decoration: underline !important;
    }

    &:active {
        color: white;
        background-color: #04132e;
        border-color: #04132e;
    }

    &:disabled {
        color: #4b4b4b;
        text-decoration: none !important;
        background-color: #c8c8c8;
        border-color: #c8c8c8;
    }

    ${({ theme, color = defaultProps.color }) => baseStyle(theme, color)}

    ${({ theme, text = defaultProps.text, color = defaultProps.color }) =>
        !text && buttonStyle(theme, color)}

        ${({
        small = defaultProps.small,
        extraSmall = defaultProps.extraSmall,
    }) =>
        small
            ? css`
                  padding: 5px 25px;
              `
            : extraSmall
            ? css`
                  padding: 5px 15px !important;
              `
            : false}

        ${({ fullWidth = defaultProps.fullWidth }) =>
        fullWidth &&
        css`
            display: block;
            width: 100%;
        `}

        ${({ theme, text = defaultProps.text, color = defaultProps.color }) =>
        text &&
        css`
            color: ${theme.colors[
                color as keyof Theme["colors"]
            ].toString()} !important;
            background-color: transparent !important;
            border: 0 !important;
        `}
`;

export default Button;
